import React, { useEffect, useState, useMemo, useRef } from 'react';
import { FiLock, FiCheck } from 'react-icons/fi';
import { FaFlask } from 'react-icons/fa';
import {
  getLabBasePrice,
  getLabDiscountAmount,
  getStep3Readiness,
  validateStep2Details,
  validateStep3Collection,
} from '../services/labCheckoutFlow';
import useLabCheckoutSession from '../hooks/useLabCheckoutSession';
import useLabCheckoutIdentity from '../hooks/useLabCheckoutIdentity';
import useLabCheckoutData from '../hooks/useLabCheckoutData';
import useLabCheckoutCollection from '../hooks/useLabCheckoutCollection';
import useLabCheckoutAoe from '../hooks/useLabCheckoutAoe';
import useLabCoupon from '../hooks/useLabCoupon';
import useLabCheckoutSubmit from '../hooks/useLabCheckoutSubmit';
import useLabCheckoutPersistence from '../hooks/useLabCheckoutPersistence';
import LabCheckoutProgress from './wizard/LabCheckoutProgress';
import LabCheckoutStepContent from './wizard/LabCheckoutStepContent';
import LabCheckoutConfirmed from './wizard/LabCheckoutConfirmed';
import { isPatientAddressComplete, verifyPatientAddress } from '../services/patientAddress';
import { getPublicLabBrandSettings } from '../services/labApi';
import { mergeLabCheckoutBranding, resolveLabCheckoutBranding } from './labCheckoutBranding';
import './LabCheckoutWizard.css';

const STEP_HEADINGS = { 1: 'Your panel', 2: 'Your details', 3: 'Collection', 4: 'Answer a few questions from the lab', 5: 'Review & pay' };

export default function LabCheckoutPage({ slug, clientConfig }) {
  const clientId = localStorage.getItem('welliemd_client_id') || 'default';
  const session = useLabCheckoutSession(slug, clientId);
  const restoredSessionRef = useRef(false);
  const paymentStepRef = useRef(null);

  const [step, setStep] = useState(1);
  const [formError, setFormError] = useState('');
  const [addressVerifying, setAddressVerifying] = useState(false);
  const [consentHipaa, setConsentHipaa] = useState(false);
  const [consentTerms, setConsentTerms] = useState(false);
  const [consentTele, setConsentTele] = useState(false);
  const [brandLogoFailed, setBrandLogoFailed] = useState(false);
  const [tenantBranding, setTenantBranding] = useState(null);

  useEffect(() => {
    let isCurrent = true;
    getPublicLabBrandSettings(clientConfig?.api_endpoint)
      .then((branding) => {
        if (isCurrent) setTenantBranding(branding);
      })
      .catch(() => {
        // The control-plane config remains the safe fallback when the tenant
        // branding endpoint is temporarily unavailable.
      });
    return () => { isCurrent = false; };
  }, [clientConfig?.api_endpoint]);

  const identity = useLabCheckoutIdentity({ clearSession: session.clear });
  const { address, setAddress } = identity;

  const data = useLabCheckoutData(slug, { onPolicyError: setFormError });
  const { lab } = data;

  const collection = useLabCheckoutCollection({
    slug,
    lab,
    getAddressState: () => address.state,
    setAddress,
    setAddressVerified: identity.setAddressVerified,
    onError: setFormError,
  });
  const { selectedMethod, availResult, availLoading } = collection;

  const basePrice = useMemo(() => getLabBasePrice(lab, selectedMethod), [lab, selectedMethod]);
  const coupon = useLabCoupon(lab, basePrice);
  const discountAmount = useMemo(
    () => getLabDiscountAmount(coupon.appliedCoupon, basePrice),
    [coupon.appliedCoupon, basePrice],
  );
  const total = useMemo(() => Math.max(0, basePrice - discountAmount), [basePrice, discountAmount]);

  const aoe = useLabCheckoutAoe(lab, selectedMethod);

  const submit = useLabCheckoutSubmit({
    slug, lab,
    patientId: identity.patientId, dob: identity.dob, sex: identity.sex, phone: identity.phone,
    address, zip: collection.zip,
    selectedMethod, selectedDrawSite: collection.selectedDrawSite, availResult,
    checkoutAoeQuestions: aoe.checkoutAoeQuestions, aoeAnswers: aoe.aoeAnswers,
    missingAoeQuestions: aoe.missingAoeQuestions,
    consentHipaa, consentTerms, consentTele,
    checkoutPoliciesMissing: data.checkoutPoliciesMissing, total,
    appliedCoupon: coupon.appliedCoupon, paymentStepRef,
    setFormError,
    clearSession: session.clear,
    clearAuthenticatedState: identity.clearAuthenticatedState,
    setDetailsMode: identity.setDetailsMode,
    setStep,
  });

  useLabCheckoutPersistence({
    session,
    restoredRef: restoredSessionRef,
    values: {
      step, detailsMode: identity.detailsMode, email: identity.email, phone: identity.phone,
      dob: identity.dob, sex: identity.sex, zip: collection.zip, address,
      selectedMethod, selectedDrawSite: collection.selectedDrawSite,
      couponCode: coupon.couponCode, appliedCoupon: coupon.appliedCoupon,
      aoeAnswers: aoe.aoeAnswers, consentHipaa, consentTerms, consentTele,
    },
    setters: {
      setStep, setDetailsMode: identity.setDetailsMode, setEmail: identity.setEmail,
      setPhone: identity.setPhone, setDob: identity.setDob, setSex: identity.setSex,
      setZip: collection.setZip, setAddress, setSelectedDrawSite: collection.setSelectedDrawSite,
      setCouponCode: coupon.setCouponCode, setAppliedCoupon: coupon.setAppliedCoupon,
      setAoeAnswers: aoe.setAoeAnswers,
      setConsentHipaa, setConsentTerms, setConsentTele,
    },
    collection: {
      lab, availResult, availLoading, handleCheckAvailability: collection.handleCheckAvailability,
    },
  });

  const canProceedStep1 = Boolean(lab);
  const canProceedStep2 = validateStep2Details({
    email: identity.email, phone: identity.phone, dob: identity.dob, sex: identity.sex,
  });
  const { needsDrawSite, canProceed: canProceedStep3 } = getStep3Readiness({
    selectedMethod,
    availResult,
    address,
    selectedDrawSite: collection.selectedDrawSite,
    zip: collection.zip,
  });

  const handleNext = async () => {
    setFormError('');
    if (step === 2 && !canProceedStep2) {
      setFormError('Please complete email, phone, date of birth, and sex at birth.');
      return;
    }
    if (step === 2 && !identity.isAuthenticated) {
      if (identity.detailsMode === 'account') {
        setFormError('Please sign in before continuing.');
        return;
      }
      const created = await identity.handleCreateAccountAndLogin();
      if (!created.ok) {
        if (!created.handled) {
          setFormError(created.message || 'Create your account before continuing.');
        }
        return;
      }
    }
    if (step === 2 && identity.isAuthenticated) {
      const saved = await identity.savePatientDetails();
      if (!saved.ok) {
        setFormError(saved.message || 'Could not save patient details.');
        return;
      }
    }
    if (step === 3) {
      const step3Validation = validateStep3Collection({
        selectedMethod,
        availResult,
        address,
        selectedDrawSite: collection.selectedDrawSite,
        zip: collection.zip,
      });
      if (!step3Validation.isValid) {
        setFormError(step3Validation.error || 'Please complete all requirements for Step 3.');
        return;
      }
      if (!identity.addressVerified) {
        setAddressVerifying(true);
        setFormError('');
        try {
          const verifyResult = await verifyPatientAddress(address);
          if (!verifyResult.isValid || !verifyResult.verified) {
            setFormError(verifyResult.error || 'Patient address not recognized. Please check your street address, city, state, and ZIP code before continuing.');
            return;
          }
          if (verifyResult.normalizedAddress) {
            setAddress(verifyResult.normalizedAddress);
          }
          identity.setAddressVerified(true);
        } catch {
          setFormError('Patient address verification failed. Please check your address details.');
          return;
        } finally {
          setAddressVerifying(false);
        }
      }
    }
    if (step === 4 && aoe.missingAoeQuestions.length > 0) {
      setFormError('Please answer all required lab questions.');
      setTimeout(() => {
        const firstMissing = aoe.missingAoeQuestions[0];
        if (firstMissing) {
          const key = `${firstMissing.markerId}:${firstMissing.questionId}`;
          const el = document.querySelector(`.lcw-aoe-card[data-aoe-key="${key}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 0);
      return;
    }
    setStep(s => Math.min(s + 1, 5));
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    if (identity.authLoading || availLoading || addressVerifying || submit.isSubmitting) return;
    setFormError('');
    setStep(s => Math.max(s - 1, 1));
    window.scrollTo(0, 0);
  };

  const resolvedClientConfig = mergeLabCheckoutBranding(clientConfig, tenantBranding);
  const { name: logoName, logoUrl } = resolveLabCheckoutBranding(resolvedClientConfig);
  useEffect(() => {
    setBrandLogoFailed(false);
  }, [logoUrl]);
  const displayBrandLogo = logoUrl && !brandLogoFailed;
  const logoInitial = logoName.charAt(0).toUpperCase();

  if (data.labLoading) {
    return <div className="lcw"><div className="lcw-spinner">Loading checkout…</div></div>;
  }
  if (data.labError || !lab) {
    return (
      <div className="lcw">
        <div className="lcw-main"><div className="lcw-error">{data.labError || 'Lab not found.'}</div></div>
      </div>
    );
  }
  if (submit.confirmedOrder) {
    return (
      <LabCheckoutConfirmed
        confirmedOrder={submit.confirmedOrder}
        lab={lab}
        selectedMethod={selectedMethod}
        logoInitial={logoInitial}
        logoName={logoName}
        logoUrl={logoUrl}
        patientPortalDomain={clientConfig?.patient_portal_domain}
      />
    );
  }

  const stepBusy = identity.authLoading || availLoading || addressVerifying || submit.isSubmitting
    || (step === 5 && (data.paymentConfigLoading || identity.savedPaymentMethodsLoading));
  const continueLabel = step === 2 && identity.authLoading
    ? (identity.detailsMode === 'new' && !identity.isAuthenticated ? 'Creating account…' : 'Signing in…')
    : step === 3 && availLoading
      ? 'Checking availability…'
      : step === 3 && addressVerifying
        ? 'Verifying address…'
        : 'Continue';

  const detailsProps = {
    isAuthenticated: identity.isAuthenticated, patientName: identity.patientName,
    patientEmail: identity.patientEmail, detailsMode: identity.detailsMode,
    setDetailsMode: identity.setDetailsMode, email: identity.email, setEmail: identity.setEmail,
    phone: identity.phone, setPhone: identity.setPhone, dob: identity.dob, setDob: identity.setDob,
    sex: identity.sex, setSex: identity.setSex, loginPassword: identity.loginPassword,
    setLoginPassword: identity.setLoginPassword, newPassword: identity.newPassword,
    setNewPassword: identity.setNewPassword, handleLogin: identity.handleLogin,
    handleLogout: identity.handleLogout, authLoading: identity.authLoading, authError: identity.authError,
  };
  const collectionProps = {
    zip: collection.zip, handleZipChange: collection.handleZipChange, availResult, availLoading,
    handleCheckAvailability: collection.handleCheckAvailability, selectedMethod,
    setSelectedMethod: collection.setSelectedMethod, address, setAddress,
    addressVerified: identity.addressVerified, setAddressVerified: identity.setAddressVerified,
    onStateChange: collection.handleStateChange,
    selectedDrawSite: collection.selectedDrawSite, setSelectedDrawSite: collection.setSelectedDrawSite,
  };
  const labQuestionsProps = {
    questions: aoe.checkoutAoeQuestions, aoeAnswers: aoe.aoeAnswers, setAoeAnswers: aoe.setAoeAnswers, 
    missingAoeQuestions: aoe.missingAoeQuestions, formError,
  };
  const paymentProps = {
    total, basePrice, couponCode: coupon.couponCode, setCouponCode: coupon.setCouponCode,
    appliedCoupon: coupon.appliedCoupon, setAppliedCoupon: coupon.setAppliedCoupon,
    couponError: coupon.couponError, couponLoading: coupon.couponLoading,
    handleApplyCoupon: coupon.handleApplyCoupon, consentHipaa, setConsentHipaa,
    consentTerms, setConsentTerms, consentTele, setConsentTele,
    paymentConfig: data.paymentConfig, paymentConfigLoading: data.paymentConfigLoading,
    savedPaymentMethods: identity.savedPaymentMethods,
    savedPaymentMethodsLoading: identity.savedPaymentMethodsLoading,
    paymentStepRef, isSubmitting: submit.isSubmitting, formError,
    checkoutPolicies: data.checkoutPolicies, checkoutPoliciesLoading: data.checkoutPoliciesLoading,
    checkoutPoliciesMissing: data.checkoutPoliciesMissing, handlePlaceOrder: submit.handlePlaceOrder,
  };

  return (
    <div className="lcw">
      <header className="lcw-header">
        <div className="lcw-header-inner">
          <div className="lcw-logo">
            {displayBrandLogo ? (
              <img
                className="lcw-logo-image"
                src={logoUrl}
                alt={logoName}
                onError={() => setBrandLogoFailed(true)}
              />
            ) : (
              <div className="lcw-logo-icon" aria-hidden="true"><FaFlask size={15} /></div>
            )}
          </div>
          <div className="lcw-secure"><FiLock size={13} /> Secure checkout</div>
        </div>
      </header>

      <div className="lcw-main">
        <p className="lcw-step-eyebrow">Step {step} of 5</p>
        <h1 className="lcw-step-heading">{STEP_HEADINGS[step]}</h1>
        <div className="lcw-progress-wrap">
          <LabCheckoutProgress current={step} />
        </div>

        <LabCheckoutStepContent
          step={step}
          lab={lab}
          details={detailsProps}
          collection={collectionProps}
          labQuestions={labQuestionsProps}
          payment={paymentProps}
        />

        {formError && step < 4 && !(step === 3 && (
          (availResult && !availResult.is_available) ||
          (address?.state && availResult?.resolved_state && address.state.trim().toUpperCase() !== availResult.resolved_state.trim().toUpperCase()) ||
          (availResult && address?.zip && collection.zip && address.zip.trim() !== collection.zip.trim())
        )) && (
          <div className="lcw-error lcw-error-top">{formError}</div>
        )}

        <div className="lcw-footer">
          <div className="lcw-footer-inner">
            {step === 4 && (
              (() => {
                const requiredQuestions = (aoe.checkoutAoeQuestions || []).filter(q => q.required);
                const totalRequired = requiredQuestions.length;
                const answeredRequired = requiredQuestions.filter(q => {
                  const key = `${q.markerId}:${q.questionId}`;
                  const val = aoe.aoeAnswers?.[key];
                  return val !== undefined && val !== null && val !== '';
                }).length;
                if (totalRequired === 0) return null;
                return (
                  <div className="lcw-footer-progress">
                    <FiCheck size={16} color={answeredRequired === totalRequired ? '#16a34a' : '#94a3b8'} />
                    <span>{answeredRequired} of {totalRequired} required answered</span>
                  </div>
                );
              })()
            )}
            <div className="lcw-footer-buttons">
              {step > 1 && (
                <button className="lcw-btn-back" onClick={handleBack} disabled={stepBusy}>
                  &larr; Back
                </button>
              )}
              {step < 5 && (
                <button
                  className="lcw-btn-continue"
                  onClick={handleNext}
                  disabled={(step === 1 ? !canProceedStep1 : false) || stepBusy}
                >
                  {step === 4 ? 'Review & pay' : continueLabel} &rarr;
                </button>
              )}
              {step === 5 && (
                <button
                  className="lcw-btn-continue lcw-btn-icon"
                  onClick={submit.handlePlaceOrder}
                  disabled={stepBusy}
                >
                  <FiLock size={15} />
                  {submit.isSubmitting ? 'Processing…' : `Pay & place order - $${total.toFixed(2)}`}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
